import type { SolutionItem } from "./SolutionFinder/types"

import { useGetStoryblokBundle } from "@hooks/V2/storyblok/useBundle"
import {
  isConsolidatedPurchasableItem,
  useGetConsolidatedItem,
} from "@hooks/V2/storyblok/useConsolidatedItem"
import { storyblokItemFromConfig } from "@hooks/V2/storyblok/useItem"
import { useGetStoryblokOnlineProduct } from "@hooks/V2/storyblok/useOnlineProduct"
import { useGetStoryblokProduct } from "@hooks/V2/storyblok/useProduct"

export type ResolvedCombinedOnlineItem = {
  kind: "combinedOnline"
  item: SolutionItem
}

export type ResolvedOnlineProductItem = {
  kind: "onlineProduct"
  item: SolutionItem
  config: Storyblok.OnlineProductConfig
  consolidatedItem: YocoCom.ConsolidatedOnlineProductItem
}

export type ResolvedPurchasableItem = {
  kind: "purchasable"
  item: SolutionItem
  config: Storyblok.ProductConfig | Storyblok.BundleConfig
  consolidatedItem: YocoCom.ConsolidatedPurchasableItem
}

export type ResolvedSolutionItem =
  | ResolvedCombinedOnlineItem
  | ResolvedOnlineProductItem
  | ResolvedPurchasableItem

export const useResolvedSolution = (
  solution: SolutionItem[]
): ResolvedSolutionItem[] => {
  const getStoryblokProduct = useGetStoryblokProduct()
  const getStoryblokBundle = useGetStoryblokBundle()
  const getStoryblokOnlineProduct = useGetStoryblokOnlineProduct()
  const getConsolidatedItem = useGetConsolidatedItem()

  const resolvedSolution: ResolvedSolutionItem[] = []

  for (const item of solution) {
    if (item.type === "online") {
      if (item.value === "all") {
        resolvedSolution.push({ kind: "combinedOnline", item })
        continue
      }

      const config = getStoryblokOnlineProduct(item.value, "name")

      if (!config) continue

      const consolidatedItem = getConsolidatedItem({
        _uid: "",
        component: "",
        uuid: config.uuid,
        content: config,
      })

      if (
        !consolidatedItem ||
        isConsolidatedPurchasableItem(consolidatedItem)
      ) {
        continue
      }

      resolvedSolution.push({
        kind: "onlineProduct",
        item,
        config,
        consolidatedItem,
      })
      continue
    }

    const config =
      item.type === "product"
        ? getStoryblokProduct(item.value, "sku")
        : getStoryblokBundle(item.value, "code")

    if (!config) continue

    const consolidatedItem = getConsolidatedItem(
      storyblokItemFromConfig(config.uuid, config)
    )

    if (!isConsolidatedPurchasableItem(consolidatedItem)) continue

    resolvedSolution.push({
      kind: "purchasable",
      item,
      config,
      consolidatedItem,
    })
  }

  return resolvedSolution
}
